import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { TargetAndTransition } from 'motion/react';
import './Table.css';
import { compareTableValues, TableSortButton, type TableSort, type TableSortValue } from './TableSorting';
export { TableSortButton, compareTableValues } from './TableSorting';
export type { TableSort, TableSortValue } from './TableSorting';

export type TableVariant = 'table' | 'expandable' | 'list-card';

/**
 * Mobile display mode for responsive tables
 * - 'table': Keep table layout on mobile (horizontal scroll)
 * - 'card': Transform rows into stacked cards
 * - 'hide': Hide table on mobile (show alternative content)
 */
export type TableMobileMode = 'table' | 'card' | 'hide';

export interface TableProps {
  /** Disable sorting when row order conveys meaning. */
  sortable?: boolean;
  /** Controlled sorting: the owner sorts before pagination. */
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
  columns?: number;
  variant?: TableVariant;
  children: React.ReactNode;
  ariaLabel?: string;
  striped?: boolean;
  /**
   * Optional custom grid template for columns.
   * - Provide a CSS grid template string like: "200px 1fr 2fr" or "20% 30% 50%".
   * - Or provide an array of numbers/strings:
   *   - Numbers: Decimals (0–1] are converted to percentages (e.g. 0.2 -> 20%). Integers are treated as fr units (e.g. 2 -> 2fr).
   *   - Strings are used as-is (e.g. "minmax(200px, 1fr)", "auto", "2fr", "20%").
   */
  columnTemplate?: string | Array<number | string>;
  /**
   * Enable built-in interactive behavior for rows in <tbody>.
   * Adds hover/active/focus styles and keyboard activation.
   */
  rowInteractive?: boolean;
  /**
   * Handle row clicks using event delegation.
   * Returns the clicked row and its original 0-based index before local sorting.
   */
  onRowClick?: (args: {
    rowElement: HTMLTableRowElement;
    rowIndex: number;
    event: React.MouseEvent;
  }) => void;
  /**
   * When true, tbody rows become focusable (tabIndex=0) and Enter/Space triggers onRowClick.
   */
  enableRowKeyboard?: boolean;
  /**
   * Mobile display mode. Defaults to 'table' (horizontal scroll).
   * Set to 'card' to transform rows into stacked cards on mobile.
   */
  mobileMode?: TableMobileMode;
}

type CSSVarMap = { [K in `--${string}`]?: string | number };

const TableContext = React.createContext<{
  rowInteractive?: boolean;
  sortable?: boolean;
  sort?: TableSort;
  controlled?: boolean;
  onSortChange?: (sort: TableSort) => void;
}>({});

function childElements(children: React.ReactNode, keyPrefix = ''): React.ReactElement[] {
  return React.Children.toArray(children).flatMap(child => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return [];
    if (child.type === React.Fragment) return childElements(child.props.children, `${keyPrefix}${child.key}/`);
    return [keyPrefix ? React.cloneElement(child, { key: `${keyPrefix}${child.key}` }) : child];
  });
}

function cellText(children: React.ReactNode): string {
  return React.Children.toArray(children).map(child => {
    if (typeof child === 'string' || typeof child === 'number') return String(child);
    return React.isValidElement<{ children?: React.ReactNode }>(child) ? cellText(child.props.children) : '';
  }).join(' ').trim();
}

export const Table = React.memo(function Table({
  sortable = true,
  sort: controlledSort,
  onSortChange,
  columns = 4,
  variant = 'table',
  children,
  ariaLabel,
  striped,
  columnTemplate,
  rowInteractive,
  onRowClick,
  enableRowKeyboard,
  mobileMode = 'table',
}: TableProps) {
  const tableRef = React.useRef<HTMLTableElement | null>(null);
  const [localSort, setLocalSort] = React.useState<TableSort>({ key: null, direction: 'asc' });
  const sort = controlledSort ?? localSort;

  // Apply interactive attributes and keyboard affordances to tbody rows
  React.useEffect(() => {
    if (!tableRef.current) return;
    const table = tableRef.current;
    const rows = table.querySelectorAll('[data-section="body"] [data-row="true"]');
    rows.forEach((row) => {
      const tr = row as HTMLTableRowElement;
      const hasExplicitInteractive = tr.getAttribute('data-interactive') === 'true';
      const shouldBeInteractive = rowInteractive || hasExplicitInteractive;

      if (shouldBeInteractive) {
        tr.setAttribute('data-interactive', 'true');
        if (enableRowKeyboard && !tr.hasAttribute('tabindex')) tr.tabIndex = 0;
      } else if (enableRowKeyboard && tr.tabIndex === 0) {
        // Only clean up keyboard affordance if not interactive at all
        tr.removeAttribute('tabindex');
      }
      // Do not forcibly remove data-interactive when rowInteractive is false;
      // this allows per-row `interactive` to opt-in.
    });
  }, [rowInteractive, enableRowKeyboard, children]);

  const handleClick = React.useCallback(
    (event: React.MouseEvent) => {
      if (!onRowClick) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const row = target.closest('[data-row="true"]');
      const section = target.closest('[data-section="body"]');
      if (!row || !section) return;
      // Ignore clicks that originate from interactive descendants within the row
      const interactiveSelector = [
        '[data-row-click-stop]',
        'button',
        'a[href]',
        'input:not([type="hidden"])',
        'select',
        'textarea',
        '[role="button"]',
        '[role="link"]',
        '[contenteditable="true"]',
        '[tabindex]:not([tabindex="-1"]):not([data-row="true"])',
      ].join(', ');
      const interactiveWithin = target.closest(interactiveSelector);
      if (interactiveWithin && (row as Element).contains(interactiveWithin)) return;
      // Keep callbacks indexed into source data when local sorting moves rows.
      const rows = Array.from(section.querySelectorAll(':scope > [data-row="true"]'));
      const rowIndex = Number(row.getAttribute('data-table-source-index') ?? rows.indexOf(row as HTMLTableRowElement));
      if (rowIndex === -1) return;
      onRowClick({ rowElement: row as HTMLTableRowElement, rowIndex, event });
    },
    [onRowClick],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (!onRowClick || !enableRowKeyboard) return;
      const isActivation = event.key === 'Enter' || event.key === ' ';
      if (!isActivation) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const row = target.closest('[data-row="true"]');
      const section = target.closest('[data-section="body"]');
      if (!row || !section) return;
      // Ignore keyboard activation originating from interactive descendants within the row
      const interactiveSelector = [
        '[data-row-click-stop]',
        'button',
        'a[href]',
        'input:not([type="hidden"])',
        'select',
        'textarea',
        '[role="button"]',
        '[role="link"]',
        '[contenteditable="true"]',
        '[tabindex]:not([tabindex="-1"]):not([data-row="true"])',
      ].join(', ');
      const interactiveWithin = target.closest(interactiveSelector);
      if (interactiveWithin && (row as Element).contains(interactiveWithin)) return;
      const rows = Array.from(section.querySelectorAll(':scope > [data-row="true"]'));
      const rowIndex = Number(row.getAttribute('data-table-source-index') ?? rows.indexOf(row as HTMLTableRowElement));
      if (rowIndex === -1) return;
      event.preventDefault();
      // Synthesize a click-like callback
      onRowClick({
        rowElement: row as HTMLTableRowElement,
        rowIndex,
        event: event as unknown as React.MouseEvent,
      });
    },
    [onRowClick, enableRowKeyboard],
  );

  let resolvedTemplate: string | undefined;

  if (typeof columnTemplate === 'string') {
    resolvedTemplate = columnTemplate;
  } else if (Array.isArray(columnTemplate)) {
    resolvedTemplate = columnTemplate
      .map((val) => {
        if (typeof val === 'string') return val;
        if (!Number.isFinite(val)) return '1fr';
        const num = Number(val);
        if (num > 0 && num <= 1) {
          return `${(num * 100).toFixed(6).replace(/\.0+$/, '')}%`;
        }
        return `${num}fr`;
      })
      .join(' ');
  }

  return (
    <div
      className="table"
      ref={tableRef}
      data-columns={columns}
      data-variant={variant}
      data-mobile-mode={mobileMode}
      aria-label={ariaLabel}
      data-striped={striped}
      data-has-template={resolvedTemplate ? 'true' : undefined}
      style={
        resolvedTemplate
          ? ({
              ['--sl-table-template' as unknown as 'color']: resolvedTemplate,
            } as React.CSSProperties)
          : undefined
      }
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="grid"
      tabIndex={0}
    >
      <TableContext.Provider value={{ rowInteractive, sortable, sort, controlled: Boolean(onSortChange), onSortChange: onSortChange ?? setLocalSort }}>
        {children}
      </TableContext.Provider>
    </div>
  );
});

// Compound components rendered as divs with roles, to keep styling flexible and animation-friendly.
export interface TableSectionProps {
  children: React.ReactNode;
}

export function TableHeader({ children }: TableSectionProps) {
  return (
    <div className="table__header" role="rowgroup" data-section="header">
      {children}
    </div>
  );
}

export function TableBody({ children }: TableSectionProps) {
  const { sort, controlled, sortable } = React.useContext(TableContext);
  const rows = childElements(children).map((row, index) => ({ row, index }));
  const locallySorted = sortable && !controlled && sort?.key !== null && sort?.key !== undefined;
  if (locallySorted) {
    const column = Number(sort.key);
    const value = (row: React.ReactElement) => {
      const cells = childElements((row.props as TableRowProps).children);
      const cell = cells[column] as React.ReactElement<TableCellProps> | undefined;
      return cell && 'sortValue' in cell.props ? cell.props.sortValue : cellText(cell?.props.children);
    };
    rows.sort((a, b) => compareTableValues(value(a.row), value(b.row), sort.direction));
  }
  return (
    <div className="table__body" role="rowgroup" data-section="body">
      {controlled ? children : rows.map(({ row, index }) => React.cloneElement(row as React.ReactElement<TableRowProps>, { 'data-table-source-index': index }))}
    </div>
  );
}

export interface TableRowProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  interactive?: boolean;
  [key: `data-${string}`]: string | number | boolean | undefined;
}

export function TableRow({ children, interactive, className, style, ...rest }: TableRowProps) {
  const { rowInteractive: contextInteractive } = React.useContext(TableContext);
  const isInteractive = interactive ?? contextInteractive ?? false;

  const interactiveStyle: (React.CSSProperties & CSSVarMap) | undefined = isInteractive
    ? { willChange: 'transform', ['--sl-row-press-scale']: 1, ...style } as React.CSSProperties & CSSVarMap
    : style as (React.CSSProperties & CSSVarMap) | undefined;

  const hoverTarget: CSSVarMap | undefined = isInteractive
    ? { ['--sl-row-press-scale']: 1.005 }
    : undefined;

  const tapTarget: CSSVarMap | undefined = isInteractive
    ? { ['--sl-row-press-scale']: 0.985 }
    : undefined;

  const dataProps: Record<string, string | number | boolean | undefined> = {};
  for (const key in rest) {
    if (key.startsWith('data-')) {
      dataProps[key] = rest[key as `data-${string}`];
    }
  }

  return (
    <motion.div
      className={`table__row${className ? ` ${className}` : ''}`}
      role="row"
      data-row="true"
      data-interactive={isInteractive ? 'true' : undefined}
      style={interactiveStyle}
      whileHover={hoverTarget as unknown as TargetAndTransition}
      whileTap={tapTarget as unknown as TargetAndTransition}
      transition={isInteractive ? { type: 'spring', bounce: 0.25, duration: 0.25 } : undefined}
      {...dataProps}
    >
      {childElements(children).map((child, index) => child.type === TableHeaderCell
        ? React.cloneElement(child as React.ReactElement<TableHeaderCellProps>, { columnIndex: index })
        : child)}
    </motion.div>
  );
}

export interface ExpandableTableRowProps {
  'data-table-source-index'?: number;
  children: React.ReactNode;
  interactive?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  expansionContent?: React.ReactNode;
}

export function ExpandableTableRow({
  children,
  interactive = true,
  expanded = false,
  onToggle,
  expansionContent,
  'data-table-source-index': sourceIndex,
}: ExpandableTableRowProps) {
  const { rowInteractive: contextInteractive } = React.useContext(TableContext);
  const isInteractive = interactive ?? contextInteractive ?? true;

  const interactiveStyle: (React.CSSProperties & CSSVarMap) | undefined = isInteractive
    ? { willChange: 'transform', ['--sl-row-press-scale']: 1 }
    : undefined;

  const hoverTarget: CSSVarMap | undefined = isInteractive
    ? { ['--sl-row-press-scale']: 1.005 }
    : undefined;

  const tapTarget: CSSVarMap | undefined = isInteractive
    ? { ['--sl-row-press-scale']: 0.985 }
    : undefined;

  const handleRowClick = React.useCallback(
    (event: React.MouseEvent) => {
      // Stop propagation to prevent table-level row click handling
      event.stopPropagation();

      // Check if the click target is an interactive element or within one
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const interactiveSelector = [
        'button',
        'a[href]',
        'input:not([type="hidden"])',
        'select',
        'textarea',
        '[role="button"]',
        '[role="link"]',
        '[contenteditable="true"]',
        '[data-row-click-stop]',
      ].join(', ');

      const interactiveWithin = (target as Element).closest(interactiveSelector);
      if (interactiveWithin && (event.currentTarget as Element).contains(interactiveWithin)) {
        // Don't trigger expansion if clicking on or within an interactive element
        return;
      }

      onToggle?.();
    },
    [onToggle],
  );

  return (
    <div className="table__expandable-row" data-expandable="true">
      <motion.div
        className="table__row"
        role="row"
        data-row="true"
        data-interactive={isInteractive ? 'true' : undefined}
        data-expanded={expanded ? 'true' : undefined}
        data-table-source-index={sourceIndex}
        style={interactiveStyle}
        whileHover={hoverTarget as unknown as TargetAndTransition}
        whileTap={tapTarget as unknown as TargetAndTransition}
        transition={isInteractive ? { type: 'spring', bounce: 0.25, duration: 0.25 } : undefined}
        onClick={handleRowClick}
      >
        {children}
      </motion.div>
      <AnimatePresence>
        {expanded && expansionContent && (
          <motion.div
            className="table__expansion-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.3,
              opacity: { duration: 0.2 },
            }}
            style={{ overflow: 'hidden' }}
          >
            {expansionContent}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export interface TableExpansionContentProps {
  children: React.ReactNode;
}

export function TableExpansionContent({ children }: TableExpansionContentProps) {
  return <div className="table__expansion-inner">{children}</div>;
}

export interface TableHeaderCellProps {
  children: React.ReactNode;
  compact?: boolean;
  sortable?: boolean;
  sortKey?: string;
  /** Accessible name for a composed header. */
  sortLabel?: string;
  /** Assigned by TableRow after hidden columns have been removed. */
  columnIndex?: number;
}

export function TableHeaderCell({ children, compact, sortable, sortKey, sortLabel, columnIndex }: TableHeaderCellProps) {
  const context = React.useContext(TableContext);
  const label = sortLabel ?? cellText(children);
  const key = context.controlled ? sortKey : columnIndex === undefined ? undefined : String(columnIndex);
  const canSort = context.sortable && sortable !== false && Boolean(label) && label.toLowerCase() !== 'actions'
    && (!compact || sortable === true) && key !== undefined;
  const direction = canSort && context.sort?.key === key ? context.sort.direction : undefined;
  return (
    <div className="table__header-cell" role="columnheader" data-compact={compact || undefined}
      aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : undefined}>
      {canSort ? <TableSortButton label={label} direction={direction}
        onClick={() => context.onSortChange?.({ key, direction: direction === 'asc' ? 'desc' : 'asc' })}>
        {children}
      </TableSortButton> : children}
    </div>
  );
}

export interface TableCellProps {
  /** Raw value for formatted dates, ratings, icon-only status, and composed cells. */
  sortValue?: TableSortValue;
  children: React.ReactNode;
  /** Label shown in mobile card view */
  mobileLabel?: string;
  /** Hide this cell in mobile card view */
  mobileHidden?: boolean;
  /** Make this cell the card title in mobile view */
  mobileTitle?: boolean;
  /** Use tighter padding for narrow columns (checkbox, actions) */
  compact?: boolean;
}

export function TableCell({ children, mobileLabel, mobileHidden, mobileTitle, compact }: TableCellProps) {
  return (
    <div
      className="table__cell"
      role="cell"
      data-mobile-label={mobileLabel}
      data-mobile-hidden={mobileHidden || undefined}
      data-mobile-title={mobileTitle || undefined}
      data-compact={compact || undefined}
    >
      {mobileLabel && <span className="table__cell-label">{mobileLabel}</span>}
      <span className="table__cell-value">{children}</span>
    </div>
  );
}
